// Trace review in Settings: the review standards, each switched on or off,
// and the norms and intents of the organization's review profile, read-only
// (the profile's build writes them). Each row is its name over its gist, and
// how much it did in the last 30 days as a pale bar and a count; its full
// text opens under it. The busiest are listed first.
import { useCallback, useEffect, useMemo, useState } from "react";
import { instructionOf, listStandards, setStandardLive, triageErrorText } from "../../lib/findingsTriage.js";
import { hasPermission, useMyPermissions } from "../../lib/permissions.js";
import { PERMISSIONS } from "../../lib/permissionKeys.js";
import { getReviewProfile, updateReviewPolicy } from "../../lib/reviewClient.js";
import { MAX_POLICY_CHARS, parseConstitution } from "../../lib/traceEvaluation.js";
import { WINDOW_DAYS, readMissed, readMissedFindings, readProfileCounts, readStandardCounts } from "../../lib/traceReviewCounts.js";
import { parseServerTimestamp } from "../../lib/traceUtils.js";
import HoverTooltip from "../HoverTooltip.jsx";
import Button from "../ui/Button.jsx";
import Chevron from "../ui/Chevron.jsx";
import Field from "../ui/Field.jsx";
import Listing from "../ui/Listing.jsx";
import { ErrorText, SaveStatus } from "../ui/Notice.jsx";
import { SkeletonBar, SkeletonRows } from "../ui/Skeleton.jsx";
import { FIELD } from "../ui/styles.js";
import Switch from "../ui/Switch.jsx";
import SectionActions, { SECTION_BUTTON } from "./SectionActions.jsx";
import { PAGE_PART } from "./Page.jsx";

const MUTED = "text-[color:var(--muted)]";
const NUM = new Intl.NumberFormat("en-US");
const plural = (n, one, many = `${one}s`) => `${NUM.format(n)} ${n === 1 ? one : many}`;
const LIST = "grid max-w-[880px] min-w-0";
// Name, measure (a bar and a count; a phone drops the bar), and the switch where there is one.
const GRID = { switch: "grid-cols-[minmax(0,1fr)_minmax(68px,228px)_44px]", plain: "grid-cols-[minmax(0,1fr)_minmax(68px,228px)]" };
const MEASURE = "grid grid-cols-[minmax(0,1fr)_56px] items-center gap-x-[12px] max-[520px]:grid-cols-[56px]";
const LOADING = <SkeletonRows rows={5} cells={["w-[45%]", "ml-auto w-[30px]"]} rowClassName="grid h-[58px] grid-cols-2 items-center gap-[16px] border-b border-[color:var(--line-soft)]" />;

// The counts for a list, read once: undefined while reading, null when the read failed.
function useCounts(read) {
    const [counts, setCounts] = useState(undefined);
    useEffect(() => {
        const ac = new AbortController();
        read({ signal: ac.signal }).then(setCounts, () => { if (!ac.signal.aborted) setCounts(null); });
        return () => ac.abort();
    }, [read]);
    return counts;
}

function Head({ name, measure, grid }) {
    return (
        <div className={`grid h-[28px] items-center gap-x-[12px] border-b border-[color:var(--line)] text-[12px] ${MUTED} ${grid}`}>
            <span className="pl-[20px]">{name}</span>
            <span className="text-right">{measure}</span>
        </div>
    );
}

// What a row did: a pale bar on the list's scale and its count, the sentence on hover.
function Measure({ count, max, tip }) {
    if (count === undefined) return <span className={MEASURE}><SkeletonBar className="w-full max-[520px]:hidden" /><SkeletonBar className="ml-auto w-[24px]" /></span>;
    const known = count && count.all > 0;
    return (
        <HoverTooltip placement="top" wide label={tip}>
            <span tabIndex={0} className={`${MEASURE} rounded-[4px] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[color:var(--green)]`} data-review-measure="">
                <span className="flex h-[10px] items-center max-[520px]:hidden" aria-hidden="true">
                    {known && count.hit ? <span className="h-full min-w-[2px] rounded-[2px] bg-[color:var(--green)]/30" style={{ width: `${(count.hit / max) * 100}%` }} /> : null}
                </span>
                <span className={`text-right text-[13px] tabular-nums ${known ? "text-[color:var(--ink)]" : MUTED}`}>{known ? NUM.format(count.hit) : "—"}</span>
            </span>
        </HoverTooltip>
    );
}

// One row: its name, what sits beside it and its gist under it, opening its
// whole text; then its measure, and `end` (a switch) at the right.
function Row({ name, aside = null, gist, text, measure, end = null, grid, ...attrs }) {
    const [open, setOpen] = useState(false);
    return (
        <div className="border-b border-[color:var(--line-soft)]" {...attrs}>
            <div className={`grid min-h-[58px] items-center gap-x-[12px] ${grid}`}>
                <button
                    type="button"
                    aria-expanded={open}
                    onClick={() => setOpen(!open)}
                    className={`-ml-[6px] grid min-w-0 cursor-pointer grid-cols-[12px_minmax(0,1fr)] items-center gap-x-[8px] gap-y-[2px] rounded-[6px] px-[6px] py-[9px] text-left hover:bg-[color:var(--gray-fill)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[color:var(--green)] ${MUTED}`}
                >
                    <Chevron open={open} />
                    <span className="flex min-w-0 items-baseline gap-[8px]">
                        <span className="truncate text-[13.5px] leading-[1.35] text-[color:var(--ink)]">{name}</span>
                        {aside}
                    </span>
                    {gist && !open ? <span className="col-start-2 truncate text-[12.5px] leading-[1.4]">{gist}</span> : null}
                </button>
                {measure}
                {end ? <span className="flex justify-end">{end}</span> : null}
            </div>
            {open && typeof text === "string" ? <p className={`m-0 max-w-[72ch] whitespace-pre-line pb-[14px] pl-[26px] text-[13px] leading-[1.5] ${MUTED}`}>{text}</p> : null}
            {open && typeof text !== "string" ? <div className="pb-[14px] pl-[26px]">{text}</div> : null}
        </div>
    );
}

// The busiest first; a list whose counts aren't read keeps its order.
const byHits = (counts, keyOf) => (a, b) => (counts ? (counts.get(keyOf(b))?.hit ?? -1) - (counts.get(keyOf(a))?.hit ?? -1) : 0);
const maxOf = (counts) => Math.max(1, ...[...(counts?.values() || [])].map((c) => c.hit));

// What the standards let through, by kind of act: each opens to its newest
// findings. Kinds and titles are session content, so only a role that reads
// it sees this.
const KINDS_SHOWN = 8;
const kindName = (key) => key.replace(/[-_]+/g, " ").trim().replace(/^./, (c) => c.toUpperCase());
const SEVERITY_WORD = { critical: "Critical", high: "High" };
const dayOf = (at) => { const ms = parseServerTimestamp(at); return Number.isFinite(ms) ? new Date(ms).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : ""; };

function MissedFindings({ met, kind, hit }) {
    const [rows, setRows] = useState(undefined);
    useEffect(() => {
        const ac = new AbortController();
        readMissedFindings(met, kind, { signal: ac.signal }).then(setRows, () => { if (!ac.signal.aborted) setRows(null); });
        return () => ac.abort();
    }, [met, kind]);
    if (rows === undefined) return <SkeletonRows rows={Math.min(hit, 3)} cells={["w-[60%]", "ml-auto w-[40px]"]} rowClassName="grid h-[22px] grid-cols-2 items-center gap-[12px]" />;
    if (rows === null) return <ErrorText>These findings couldn't be read.</ErrorText>;
    return (
        <ul className="m-0 grid max-w-[72ch] list-none gap-[2px] p-0" data-review-missed-findings="">
            {rows.map((r) => (
                <li key={r.id} className="grid grid-cols-[minmax(0,1fr)_auto_52px] items-baseline gap-x-[12px] text-[12.5px] leading-[20px]">
                    <span className="truncate text-[color:var(--ink)]" title={r.title}>{r.title}</span>
                    <span className={MUTED}>{SEVERITY_WORD[r.severity] || ""}</span>
                    <span className={`text-right tabular-nums ${MUTED}`}>{dayOf(r.at)}</span>
                </li>
            ))}
            {hit > rows.length ? <li className={`text-[12px] leading-[20px] ${MUTED}`}>{plural(hit - rows.length, "older finding")}</li> : null}
        </ul>
    );
}

function MissedByStandards() {
    const [data, setData] = useState(undefined);
    const [all, setAll] = useState(false);
    useEffect(() => {
        const ac = new AbortController();
        readMissed({ signal: ac.signal }).then(setData, () => { if (!ac.signal.aborted) setData(null); });
        return () => ac.abort();
    }, []);
    const total = data ? data.kinds.reduce((n, k) => n + k.hit, 0) : 0;
    const max = Math.max(1, ...(data?.kinds || []).map((k) => k.hit));
    const shown = data ? (all ? data.kinds : data.kinds.slice(0, KINDS_SHOWN)) : [];
    return (
        <section className={`${LIST} mt-[36px]`} aria-busy={data === undefined ? "true" : undefined} data-review-missed="">
            <h3 className={`${PAGE_PART} flex items-baseline gap-[10px] pb-[10px]`}>
                Not caught by a standard
                <span className={`text-[14px] font-normal tabular-nums ${MUTED}`} data-review-missed-total="">{data === undefined ? "" : data === null ? "\u2014" : `${NUM.format(total)}${data.clipped ? "+" : ""}`}</span>
            </h3>
            {data === null ? <ErrorText>What the standards let through couldn't be read.</ErrorText>
                : data && !data.kinds.length ? <p className={`m-0 text-[13px] ${MUTED}`}>Nothing high or critical got past them in the last {WINDOW_DAYS} days.</p>
                    : (
                        <>
                            {/* On the standards' grid, so its bars line up with theirs. */}
                            <Head name="Kind of act" measure={`High and critical, ${WINDOW_DAYS} days`} grid={GRID.switch} />
                            {data ? shown.map((k) => (
                                <Row
                                    key={k.key}
                                    grid={GRID.switch}
                                    end={<span />}
                                    name={kindName(k.key)}
                                    gist={`In ${plural(k.traces, "trace")}`}
                                    text={<MissedFindings met={data.met} kind={k.key} hit={k.hit} />}
                                    measure={<Measure count={{ hit: k.hit, all: k.hit }} max={max} tip={`${plural(k.hit, "finding")} in ${plural(k.traces, "trace")} in the last ${WINDOW_DAYS} days that review confirmed and no standard met.`} />}
                                    data-review-missed-kind={k.key}
                                />
                            )) : LOADING}
                            {data && data.kinds.length > shown.length ? (
                                <button type="button" onClick={() => setAll(true)} className={`mt-[8px] h-[32px] w-fit rounded-[7px] px-[6px] text-[12.5px] ${MUTED} hover:text-[color:var(--ink)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[color:var(--green)]`} data-review-missed-more="">
                                    {plural(data.kinds.length - shown.length, "more kind")}
                                </button>
                            ) : null}
                        </>
                    )}
        </section>
    );
}

export function ReviewStandards() {
    const caps = useMyPermissions();
    const canWrite = hasPermission(caps, PERMISSIONS.TENANT_CONFIG_WRITE);
    const sensitive = hasPermission(caps, PERMISSIONS.AI_SESSIONS_READ_SENSITIVE);
    const [items, setItems] = useState(null);
    const [error, setError] = useState(null);
    const [saving, setSaving] = useState(null);
    const [saveError, setSaveError] = useState(null);
    const counts = useCounts(readStandardCounts);
    const load = useCallback(() => {
        setError(null);
        listStandards().then(setItems, (e) => setError(triageErrorText(e) || "Could not load the standards."));
    }, []);
    useEffect(load, [load]);

    const toggle = async (s, live) => {
        setSaving(s.standardId);
        setSaveError(null);
        try {
            const next = await setStandardLive({ standardId: s.standardId, expectedVersion: s.version, live });
            setItems((list) => list.map((x) => (x.standardId === next.standardId ? next : x)));
        } catch (e) {
            // A stale version means someone else changed it: show theirs.
            setSaveError(`${s.name} was not changed. ${triageErrorText(e) || "Try again."}`);
            load();
        } finally {
            setSaving(null);
        }
    };

    const tip = (c) => (c === null ? "Counts couldn't be read." : !c?.all ? `Not asked about any finding in the last ${WINDOW_DAYS} days.` : `Escalated ${plural(c.hit, "finding")} of the ${NUM.format(c.all)} it was asked about in the last ${WINDOW_DAYS} days.`);
    const sorted = items ? [...items].sort(byHits(counts, (s) => s.standardId)) : null;
    return (
        <>
        <Listing
            count={items ? items.length : error ? 0 : null}
            error={error}
            errorTitle="Standards didn't load"
            onRetry={load}
            empty={{ title: "No standards yet", note: "Write one from Findings, in the Triage view." }}
        >
            <div className={LIST} aria-busy={items ? undefined : "true"}>
                {saveError ? <ErrorText className="pb-[8px]">{saveError}</ErrorText> : null}
                <Head name="Standard" measure={`Escalated, ${WINDOW_DAYS} days`} grid={GRID.switch} />
                {sorted ? sorted.map((s) => {
                    const c = counts === undefined ? undefined : counts?.get(s.standardId) ?? null;
                    return (
                        <Row
                            key={s.standardId}
                            grid={GRID.switch}
                            name={s.name}
                            aside={<span className="shrink-0 text-[12px] tabular-nums">v{s.version}</span>}
                            gist={instructionOf(s.text)}
                            text={instructionOf(s.text)}
                            measure={<Measure count={c} max={maxOf(counts)} tip={tip(counts === null ? null : c)} />}
                            end={<Switch checked={s.live} onChange={(live) => toggle(s, live)} label={`Check new findings against ${s.name}`} disabled={!canWrite || saving === s.standardId} />}
                            data-review-standard={s.standardId}
                        />
                    );
                }) : LOADING}
            </div>
        </Listing>
        {sensitive ? <MissedByStandards /> : null}
        </>
    );
}

// What a profile's lists say: what a finding does to one, and a rating where the build gave one.
const KIND = {
    norms: { name: "Norm", measure: "Deviations", tip: (c) => `${plural(c.hit, "finding")} deviated from this norm in the last ${WINDOW_DAYS} days; ${NUM.format(c.all - c.hit)} were consistent with it.`, rating: "prevalence", words: { UNIVERSAL: "Everyone", COMMON: "Common", OCCASIONAL: "Occasional", RARE: "Rare" } },
    intents: { name: "Intent", measure: "Worked against", tip: (c) => `${plural(c.hit, "finding")} worked against this intent in the last ${WINDOW_DAYS} days; ${NUM.format(c.all - c.hit)} were aligned with it or beside it.`, rating: "weight", words: { CENTRAL: "Central", MAJOR: "Major", MINOR: "Minor" } },
};
const EMPTY = {
    norms: { title: "No norms yet", note: "They'll appear here once Origin has learned them from your traces." },
    intents: { title: "No intents yet", note: "They'll appear here once Origin has learned them from your traces." },
};
const READS = { policy: (opts) => readProfileCounts("policy", opts), norms: (opts) => readProfileCounts("norms", opts), intents: (opts) => readProfileCounts("intents", opts) };

// The written policy: its clauses as review cites them, in the document's own
// order (a policy is read in order, unlike the busiest-first lists), each with
// the findings that violated it; and, for a role that may change tenant
// settings, its text to edit. A save is version-checked against the whole
// profile, which discovery also writes: a newer version that kept the policy
// this edit started from changed only norms or intents, so the edit still
// stands and is saved over it.
const isStale = (e) => e?.status === 409 || e?.grpcCode === "ABORTED";
const SAVED = "Saved. New reviews follow it; findings already reviewed keep the policy they were judged against.";

export function ReviewPolicy() {
    const canWrite = hasPermission(useMyPermissions(), PERMISSIONS.TENANT_CONFIG_WRITE);
    const [profile, setProfile] = useState(null);
    const [error, setError] = useState(null);
    const [draft, setDraft] = useState(null);
    const [save, setSave] = useState({ status: null, error: null });
    const counts = useCounts(READS.policy);
    const load = useCallback(() => {
        setError(null);
        getReviewProfile().then(setProfile, () => setError("Could not load the review profile."));
    }, []);
    useEffect(load, [load]);
    const clauses = useMemo(() => (profile ? parseConstitution(profile.policyText) : null), [profile]);
    const editing = draft != null;
    const tooLong = editing && draft.length > MAX_POLICY_CHARS;

    const submit = async () => {
        setSave({ status: "saving", error: null });
        const write = (expectedVersion) => updateReviewPolicy({ expectedVersion, policyText: draft });
        try {
            let next;
            try {
                next = await write(profile.version);
            } catch (e) {
                if (!isStale(e)) throw e;
                const now = await getReviewProfile();
                if ((now.policyText || "") !== (profile.policyText || "")) {
                    setProfile(now);
                    setSave({ status: null, error: "Someone else saved the policy while you were editing. Save again to replace theirs with yours, or cancel to read theirs." });
                    return;
                }
                next = await write(now.version);
            }
            setProfile(next);
            setDraft(null);
            setSave({ status: "saved", error: null });
        } catch {
            setSave({ status: null, error: "The policy wasn't saved. Try again." });
        }
    };

    const tip = (c) => (counts === null ? "Counts couldn't be read." : !c?.all ? `No finding cited it in the last ${WINDOW_DAYS} days.` : `${plural(c.hit, "finding")} violated this clause in the last ${WINDOW_DAYS} days; ${NUM.format(c.all - c.hit)} were consistent with it.`);
    return (
        <>
            {canWrite && profile && !editing ? (
                <SectionActions>
                    <button type="button" className={SECTION_BUTTON} onClick={() => { setDraft(profile.policyText || ""); setSave({ status: null, error: null }); }} data-review-policy-edit="">
                        {clauses?.length ? "Edit policy" : "Write policy"}
                    </button>
                </SectionActions>
            ) : null}
            {editing ? (
                <div className="grid max-w-[880px] gap-[12px]" data-review-policy-editor="">
                    <Field
                        label="Written policy"
                        hint={`Start a line with a number (3., 5.1) to keep it as that clause's reference; other paragraphs are numbered in order. ${plural(parseConstitution(draft).length, "clause")}.`}
                        error={tooLong ? `The policy is limited to ${NUM.format(MAX_POLICY_CHARS)} characters.` : null}
                    >
                        <textarea
                            value={draft}
                            onChange={(e) => setDraft(e.target.value)}
                            aria-invalid={tooLong || undefined}
                            rows={16}
                            className={`${FIELD} min-h-[320px] resize-y py-[8px] leading-[1.5]`}
                        />
                    </Field>
                    <div className="flex items-center justify-end gap-[12px]">
                        <SaveStatus status={save.status} error={save.error} />
                        <Button onClick={() => { setDraft(null); setSave({ status: null, error: null }); }} disabled={save.status === "saving"}>Cancel</Button>
                        <Button variant="primary" onClick={submit} disabled={save.status === "saving" || tooLong} data-review-policy-save="">Save</Button>
                    </div>
                </div>
            ) : (
                <Listing
                    count={clauses ? clauses.length : error ? 0 : null}
                    error={error}
                    errorTitle="The review profile didn't load"
                    onRetry={load}
                    empty={{ title: "No written policy yet", note: canWrite ? "Write the rules your organization requires of AI work. Review cites each one by its number." : "Someone who can change tenant settings can write the rules your organization requires of AI work." }}
                >
                    <div className={LIST} aria-busy={clauses ? undefined : "true"}>
                        {save.status === "saved" ? <p role="status" className={`m-0 pb-[8px] text-[12.5px] ${MUTED}`}>{SAVED}</p> : null}
                        <Head name="Clause" measure={`Violations, ${WINDOW_DAYS} days`} grid={GRID.plain} />
                        {clauses ? clauses.map((clause) => {
                            const c = counts === undefined ? undefined : counts?.get(clause.ref) ?? null;
                            return (
                                <Row
                                    key={clause.ref}
                                    grid={GRID.plain}
                                    name={clause.text}
                                    aside={<span className="shrink-0 font-mono text-[11.5px]">{clause.ref}</span>}
                                    text={clause.text}
                                    measure={<Measure count={c} max={maxOf(counts)} tip={tip(c)} />}
                                    data-review-clause={clause.ref}
                                />
                            );
                        }) : LOADING}
                    </div>
                </Listing>
            )}
        </>
    );
}

/** The organization's norms or intents (`kind`), those findings cut against most first. */
export function ReviewProfileItems({ kind }) {
    const [profile, setProfile] = useState(null);
    const [error, setError] = useState(null);
    const counts = useCounts(READS[kind]);
    const load = useCallback(() => {
        setError(null);
        getReviewProfile().then(setProfile, () => setError("Could not load the review profile."));
    }, []);
    useEffect(load, [load]);
    const k = KIND[kind];
    const items = profile ? [...(profile[kind] || [])].sort(byHits(counts, (item) => item.reference)) : null;
    const tip = (c) => (counts === null ? "Counts couldn't be read." : !c?.all ? `No finding cited it in the last ${WINDOW_DAYS} days.` : k.tip(c));
    return (
        <Listing count={items ? items.length : error ? 0 : null} error={error} errorTitle="The review profile didn't load" onRetry={load} empty={EMPTY[kind]}>
            <div className={LIST} aria-busy={items ? undefined : "true"}>
                <Head name={k.name} measure={`${k.measure}, ${WINDOW_DAYS} days`} grid={GRID.plain} />
                {items ? items.map((item, i) => {
                    const c = counts === undefined ? undefined : counts?.get(item.reference) ?? null;
                    const rated = k.words[item[k.rating]];
                    return (
                        <Row
                            key={item.reference || i}
                            id={item.reference ? `review-${item.reference}` : undefined}
                            grid={GRID.plain}
                            name={item.title}
                            aside={<>{item.reference ? <span className="shrink-0 font-mono text-[11.5px]">{item.reference}</span> : null}{rated ? <span className="shrink-0 text-[12px]">{rated}</span> : null}</>}
                            gist={item.description}
                            text={item.description}
                            measure={<Measure count={c} max={maxOf(counts)} tip={tip(c)} />}
                            data-review-item={item.reference}
                        />
                    );
                }) : LOADING}
            </div>
        </Listing>
    );
}
